import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import jsPDF from "jspdf";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, MapPin, Calendar, User, Download, AlertCircle } from "lucide-react";

function fmtDt(iso) {
  try { return new Date(iso).toLocaleString("en-US", { dateStyle: "full", timeStyle: "short" }); }
  catch { return iso; }
}

export default function RangeBooking() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [slots, setSlots] = useState(null);
  const [mine, setMine] = useState(null);
  const [error, setError] = useState("");
  const [logoDataUrl, setLogoDataUrl] = useState("");

  useEffect(() => {
    fetch("/logo.jpg").then((r) => r.blob()).then((b) => {
      const reader = new FileReader();
      reader.onloadend = () => setLogoDataUrl(reader.result);
      reader.readAsDataURL(b);
    });
  }, []);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/range/slots"), api.get("/range/my-booking")]);
    setSlots(a.data);
    setMine(b.data);
  };
  useEffect(() => { load(); }, []);

  const book = async (slot_id) => {
    setError("");
    try { await api.post("/range/book", { slot_id }); await load(); }
    catch (e) { setError(e.response?.data?.detail || "Booking failed"); }
  };
  const cancel = async () => { await api.delete("/range/book"); await load(); };

  const downloadQualPdf = (q) => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const W = 612;
    if (logoDataUrl) { try { doc.addImage(logoDataUrl, "JPEG", 60, 60, 60, 60); } catch { /* */ } }
    doc.setLineWidth(2);
    doc.setDrawColor(15, 23, 42);
    doc.rect(36, 36, W - 72, 720);
    doc.setLineWidth(0.5);
    doc.rect(46, 46, W - 92, 700);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30, 64, 175);
    doc.text("CALIFORNIA · CCW LIVE-FIRE RANGE QUALIFICATION", W / 2, 100, { align: "center" });
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(26);
    doc.text("Range Qualification Certificate", W / 2, 150, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text("Issued upon successful completion of live-fire qualification", W / 2, 175, { align: "center" });
    doc.setLineWidth(1);
    doc.line(120, 200, W - 120, 200);

    doc.setFontSize(12);
    doc.setTextColor(51, 65, 85);
    const intro = "This document certifies that the named student completed the California CCW live-fire qualification course of fire under the supervision of the listed instructor on the date and at the location indicated below.";
    const lines = doc.splitTextToSize(intro, W - 160);
    doc.text(lines, 80, 240);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(15, 23, 42);
    doc.text(q.full_name.toUpperCase(), W / 2, 340, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text("STUDENT", W / 2, 358, { align: "center" });

    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.text("RESULT", 80, 420);
    doc.text("DATE", 80, 460);
    doc.text("LOCATION", 80, 500);
    doc.text("INSTRUCTOR", 80, 540);
    if (q.score) doc.text("SCORE", 80, 580);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(q.result === "pass" ? 21 : 153, q.result === "pass" ? 128 : 27, q.result === "pass" ? 61 : 27);
    doc.text(q.result.toUpperCase(), 250, 420);
    doc.setTextColor(15, 23, 42);
    doc.text(new Date(q.starts_at).toLocaleDateString(), 250, 460);
    doc.text(q.location, 250, 500);
    doc.text(q.instructor_name, 250, 540);
    if (q.score) doc.text(q.score, 250, 580);

    if (q.notes) {
      doc.setFontSize(10);
      doc.setTextColor(71, 85, 105);
      const n = doc.splitTextToSize(`Instructor notes: ${q.notes}`, W - 160);
      doc.text(n, 80, 620);
    }
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Qualification recorded ${new Date(q.qualified_at).toLocaleString()}`, W / 2, 735, { align: "center" });
    doc.save(`Range-Qualification-${q.full_name.replace(/\s+/g, "_")}.pdf`);
  };

  if (!slots || mine === undefined) return <div className="max-w-3xl mx-auto px-6 py-20 text-slate-500">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <button onClick={() => nav("/dashboard")} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6">
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </button>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Live-Fire Range Day</div>
      <h1 className="text-4xl font-bold tracking-tight text-slate-900 mt-2" style={{ fontFamily: "Chivo, sans-serif" }}>
        Range Qualification Booking
      </h1>
      <p className="text-slate-600 mt-3 text-sm">Select an available date with your issuing-agency-approved instructor for the live-fire qualification course of fire.</p>

      {!user?.phone && (
        <div className="mt-6 bg-amber-50 border border-amber-200 text-amber-900 text-sm px-4 py-3 rounded-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <span>Add a phone number in your profile to receive SMS confirmation and 24-hour reminders.</span>
        </div>
      )}

      {mine?.qualification && (
        <div className="mt-8 border border-slate-300 rounded-sm p-6 bg-white">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Your Qualification</div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-2">
            <div>
              <div className={`text-3xl font-bold ${mine.qualification.result === "pass" ? "text-green-700" : "text-red-700"}`} style={{ fontFamily: "Chivo, sans-serif" }}>
                {mine.qualification.result.toUpperCase()}
              </div>
              <div className="text-sm text-slate-600 mt-1">
                {new Date(mine.qualification.starts_at).toLocaleDateString()} · {mine.qualification.location} · {mine.qualification.instructor_name}
                {mine.qualification.score && <> · Score: {mine.qualification.score}</>}
              </div>
            </div>
            <button data-testid="download-qual-pdf" onClick={() => downloadQualPdf(mine.qualification)}
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-sm font-medium text-sm">
              <Download className="h-4 w-4" /> Qualification PDF
            </button>
          </div>
        </div>
      )}

      {mine?.booking && !mine?.qualification && (
        <div className="mt-8 border border-blue-200 bg-blue-50 rounded-sm p-6">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-700">Your Booking</div>
          <div className="text-lg font-semibold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>
            {fmtDt(mine.slot.starts_at)}
          </div>
          <div className="text-sm text-slate-700 mt-2 flex flex-wrap gap-x-6 gap-y-1">
            <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {mine.slot.location}</span>
            <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> {mine.slot.instructor_name}</span>
          </div>
          <button data-testid="cancel-booking" onClick={cancel} className="mt-4 text-sm text-red-700 hover:text-red-900 underline">Cancel booking</button>
        </div>
      )}

      {error && <div className="mt-6 bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2 rounded-sm">{error}</div>}

      <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Available Range Days</h2>
      {slots.length === 0 && <div className="text-sm text-slate-500">No slots posted yet. Check back soon.</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {slots.map((s) => (
          <div key={s.id} data-testid={`slot-${s.id}`} className="border border-slate-200 rounded-sm p-5 bg-white">
            <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
              <Calendar className="h-4 w-4 text-blue-700" /> {fmtDt(s.starts_at)}
            </div>
            <div className="text-xs text-slate-600 mt-2 flex flex-wrap gap-x-4 gap-y-1">
              <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.location}</span>
              <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{s.instructor_name}</span>
              <span>{s.booked}/{s.capacity} booked</span>
            </div>
            <button
              data-testid={`book-slot-${s.id}`}
              disabled={s.is_full || !!mine?.booking}
              onClick={() => book(s.id)}
              className="mt-4 w-full bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-sm font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {s.i_booked ? "Booked" : s.is_full ? "Full" : mine?.booking ? "Cancel existing booking first" : "Book this slot"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
